import { useId, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import {
  WORDMARK_VIEWBOX, WORDMARK_ONLY_HEIGHT, WORDMARK_ONE, WORDMARK_CHECKUP, WORDMARK_TAGLINE, WORDMARK_RULE,
} from "@/components/logoPaths";

const APP_NAME = import.meta.env.VITE_APP_NAME || "OneCheckUp";

// Brand colour measured from the original artwork (#00A699). The logo keeps its own teal in every theme.
const TEAL = "#00A699";

/**
 * The OneCheckUp mark: ring, "1" badge set into a gap in the ring, and the tick.
 * Source: public/brand/logo-mark.svg. Size it with className (e.g. h-12 w-12).
 */
export function LogoMark({ className, style, title }: { className?: string; style?: CSSProperties; title?: string }) {
  const maskId = `ocu-gap-${useId().replace(/:/g, "")}`;
  return (
    <svg viewBox="-2 -2 104 104" className={className} style={style} role={title ? "img" : undefined} aria-label={title} aria-hidden={title ? undefined : true}>
      <defs>
        <mask id={maskId}>
          <rect x="-10" y="-10" width="120" height="120" fill="#fff" />
          <circle cx="17" cy="17" r="17.5" fill="#000" />
        </mask>
      </defs>
      <circle cx="50" cy="50" r="44" fill="none" stroke={TEAL} strokeWidth="8" mask={`url(#${maskId})`} />
      <circle cx="17" cy="17" r="13" fill={TEAL} />
      <path d="M17.8 10.5V23.5M14.2 13.6L17.8 10.5" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M32 52L45 65L68 37" fill="none" stroke={TEAL} strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface LogoProps {
  /** Height of the mark in px; the wordmark scales with it */
  size?: number;
  /** Include "Your Health, Verified." and the teal rule under the wordmark (drop it below ~56px) */
  tagline?: boolean;
  /**
   * Colour of "CheckUp" and the tagline: "auto" follows the app theme, "light" forces the brand ink
   * (for fixed light brand surfaces), "dark" forces near-white (for fixed dark surfaces).
   */
  tone?: "auto" | "light" | "dark";
  className?: string;
}

const INK = "#212B36";
const INK_ON_DARK = "#F1F5F7";

/**
 * The full logo: the mark and the wordmark, both vector. The wordmark is traced from the original
 * artwork (scripts/trace-wordmark.py) because its typeface isn't available.
 */
export function Logo({ size = 56, tagline = false, tone = "auto", className }: LogoProps) {
  const vbH = tagline ? WORDMARK_VIEWBOX.height : WORDMARK_ONLY_HEIGHT;
  // Text block height relative to the mark, as in the original lockup
  const textH = Math.round(size * (tagline ? 0.62 : 0.4));
  // Literal classes (Tailwind only generates what it can read in the source)
  const toneClass = tone === "auto" ? "text-[#212B36] dark:text-[#F1F5F7]" : "";
  const toneStyle = tone === "light" ? { color: INK } : tone === "dark" ? { color: INK_ON_DARK } : undefined;
  return (
    <span role="img" aria-label={APP_NAME} className={cn("inline-flex items-center", className)} style={{ gap: Math.round(size * 0.2) }}>
      <LogoMark className="flex-shrink-0" style={{ width: size, height: size }} />
      <svg
        viewBox={`0 0 ${WORDMARK_VIEWBOX.width} ${vbH}`}
        style={{ height: textH, width: "auto", ...toneStyle }}
        className={cn("flex-shrink-0", toneClass)}
        aria-hidden
      >
        <path fill={TEAL} fillRule="evenodd" d={WORDMARK_ONE} />
        <path fill="currentColor" fillRule="evenodd" d={WORDMARK_CHECKUP} />
        {tagline && (
          <>
            <path fill="currentColor" fillOpacity={0.63} fillRule="evenodd" d={WORDMARK_TAGLINE} />
            <rect fill={TEAL} {...WORDMARK_RULE} />
          </>
        )}
      </svg>
    </span>
  );
}
